import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import STRINGS from "../../Constants/Strings";
import { format, parse, parseISO, getYear, getMonth, subYears } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export const EditPersonalModal = ({
  showEditModal,
  handleModalClose,
  selectedMember,
  handleSaveClick,
  applicant,
}) => {
  const modalBodyRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName:
      applicant?.member?.name ||
      (applicant?.member ? "" : selectedMember?.firstName) ||
      "",
    middleName:
      applicant?.member?.middleName ||
      (applicant?.member ? "" : selectedMember?.middleName) ||
      "",
    lastName:
      applicant?.member?.surname ||
      (applicant?.member ? "" : selectedMember?.lastName) ||
      "",
    dateOfBirth: applicant?.member?.dob
      ? format(parseISO(applicant.member.dob), "MM-dd-yyyy")
      : applicant?.member
      ? ""
      : selectedMember?.dateOfBirth
      ? format(parseISO(selectedMember.dateOfBirth), "MM-dd-yyyy")
      : "",
  });

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    setFormData({
      firstName: applicant?.member?.name || selectedMember?.firstName || "",
      middleName:
        applicant?.member?.middleName || selectedMember?.middleName || "",
      lastName: applicant?.member?.surname || selectedMember?.lastName || "",
      dateOfBirth: applicant?.member?.dob
        ? format(parseISO(applicant.member.dob), "MM-dd-yyyy")
        : selectedMember?.dateOfBirth
        ? format(parseISO(selectedMember.dateOfBirth), "MM-dd-yyyy")
        : "",
    });
  }, [applicant, selectedMember]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (["firstName", "middleName", "lastName"].includes(name)) {
      const namePattern = /^[a-zA-Z0-9\s\-]*$/;
      if (!namePattern.test(value)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    // Run your real validation logic here
    const hasErrors =
      !formData.firstName || !formData.lastName || !formData.dateOfBirth;

    if (hasErrors) {
      // Scroll to top + show errors (your parent will handle error display)
      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      // Let parent handle validation errors
      handleSaveClick(formData); // Still call it — parent shows errors
      return;
    }

    // No errors → save and close
    handleSaveClick(formData);
    handleModalClose();
  };

  return (
    <Modal
      show={showEditModal}
      onHide={handleModalClose}
      className="[ popup-modal__container ]"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="[ popup-modal__header ]" closeButton>
        <Modal.Title>
          {
            STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
              .EDIT_PERSONAL_DETAILS_MODAL.TITLE
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body ref={modalBodyRef}>
        <div className="[ row ]">
          <div className="[ col-12 ]">
            {/* First Name */}
            <div className="[ mb-3 ]">
              <label htmlFor="firstName" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_PERSONAL_DETAILS_MODAL.FIRST_NAME_LABEL
                }
              </label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                className="[ form-control ]"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </div>

            {/* Middle Name */}
            <div className="[ mb-3 ]">
              <label htmlFor="middleName" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_PERSONAL_DETAILS_MODAL.MIDDLE_NAME_LABEL
                }
              </label>
              <input
                id="middleName"
                type="text"
                name="middleName"
                className="[ form-control ]"
                placeholder={
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_PERSONAL_DETAILS_MODAL.MIDDLE_NAME_PLACEHOLDER
                }
                value={formData.middleName}
                onChange={handleInputChange}
              />
            </div>

            {/* Last Name */}
            <div className="[ mb-3 ]">
              <label htmlFor="lastName" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_PERSONAL_DETAILS_MODAL.LAST_NAME_LABEL
                }
              </label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                className="[ form-control ]"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>

            {/* Date of Birth */}
            <div className="[ mb-3 ]">
              <label htmlFor="dateOfBirth" className="[ form-label ]">
                {
                  STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                    .EDIT_PERSONAL_DETAILS_MODAL.DOB_LABEL
                }
              </label>
              <div>
                <input
                  id="dateOfBirth"
                  type="text"
                  className="[ form-control ]"
                  placeholder={
                    STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                      .EDIT_PERSONAL_DETAILS_MODAL.DOB_PLACEHOLDER
                  }
                  value={formData.dateOfBirth}
                  onFocus={() => setIsDatePickerOpen(true)}
                  style={{ marginBottom: isDatePickerOpen ? "8px" : "0" }}
                />
                {isDatePickerOpen && (
                  <DatePicker
                    selected={
                      formData.dateOfBirth
                        ? parse(formData.dateOfBirth, "MM-dd-yyyy", new Date())
                        : null
                    }
                    onChange={(date) => {
                      const formatted = date ? format(date, "MM-dd-yyyy") : "";
                      handleInputChange({
                        target: { name: "dateOfBirth", value: formatted },
                      });
                      setIsDatePickerOpen(false);
                    }}
                    minDate={subYears(new Date(), 100)}
                    maxDate={new Date()}
                    inline
                    renderCustomHeader={({
                      date,
                      changeYear,
                      changeMonth,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled,
                    }) => {
                      const years = Array.from(
                        { length: 100 },
                        (_, i) => getYear(new Date()) - i
                      );
                      const months = [
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_JAN,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_FEB,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_MAR,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_APR,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_MAY,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_JUN,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_JUL,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_AUG,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_SEP,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_OCT,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_NOV,
                        STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                          .EDIT_PERSONAL_DETAILS_MODAL.DOB_MONTHS_DEC,
                      ];
                      return (
                        <div className="custom-datepicker-header">
                          <div className="custom-datepicker-clear-section">
                            <button
                              type="button"
                              onClick={() => {
                                handleInputChange({
                                  target: { name: "dateOfBirth", value: "" },
                                });
                                setIsDatePickerOpen(false);
                              }}
                              title={
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL.DOB_CLEAR_TITLE
                              }
                              style={{
                                padding: "4px 8px",
                                border: "1px solid #dc3545",
                                background: "#dc3545",
                                color: "white",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              {
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL.DOB_CLEAR_BUTTON
                              }
                            </button>
                          </div>
                          <div className="custom-datepicker-nav-section">
                            <button
                              type="button"
                              onClick={decreaseMonth}
                              disabled={prevMonthButtonDisabled}
                              aria-label={
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL.DOB_PREV_MONTH
                              }
                            >
                              {
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL
                                  .DOB_PREV_MONTH_ICON
                              }
                            </button>
                            <select
                              value={getYear(date)}
                              onChange={(e) => changeYear(e.target.value)}
                              aria-label={
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL
                                  .DOB_YEAR_SELECT_LABEL
                              }
                            >
                              {years.map((y) => (
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              ))}
                            </select>
                            <select
                              value={months[getMonth(date)]}
                              onChange={(e) =>
                                changeMonth(months.indexOf(e.target.value))
                              }
                              aria-label={
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL
                                  .DOB_MONTH_SELECT_LABEL
                              }
                            >
                              {months.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={increaseMonth}
                              disabled={nextMonthButtonDisabled}
                              aria-label={
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL.DOB_NEXT_MONTH
                              }
                            >
                              {
                                STRINGS.BRANCH.MEMBER_PROFILE
                                  .MEMBER_QUICK_VIEW_PANEL
                                  .EDIT_PERSONAL_DETAILS_MODAL
                                  .DOB_NEXT_MONTH_ICON
                              }
                            </button>
                          </div>
                        </div>
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="[ divider ]"></div>
      </Modal.Body>

      <Modal.Footer className="[ popup-modal-footer ] [ d-block ]">
        <div className="[ mb-3 ]">
          <button
            type="button"
            className="[ submit-btn btn ] [ w-100 ]"
            onClick={handleSave}
            style={{ borderRadius: ".5em" }}
          >
            {
              STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                .EDIT_PERSONAL_DETAILS_MODAL.SAVE_BUTTON
            }
          </button>
        </div>
        <div className="[ mb-3 text-center ]">
          <span
            className="[ link ]"
            onClick={handleModalClose}
            role="presentation"
            style={{
              textDecoration: "underline",
              color: "#0090c6",
              cursor: "pointer",
            }}
          >
            {
              STRINGS.BRANCH.MEMBER_PROFILE.MEMBER_QUICK_VIEW_PANEL
                .EDIT_PERSONAL_DETAILS_MODAL.CANCEL_BUTTON
            }
          </span>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
